import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import ProductCard from './ProductCard';
import { Product } from '../features/products/productModels';
import { CategoryProduct } from '../features/categories/categoryModels';
import './ProductCarousel.css';

interface ProductCarouselProps {
  products: (Product | CategoryProduct)[];
  title?: string;
  onAddToCart: (product: Product | CategoryProduct) => void;
  onToggleWishlist?: (productId: number) => void;
  wishlistItems?: string[];
}

const ProductCarousel: React.FC<ProductCarouselProps> = ({
  products,
  title,
  onAddToCart,
  onToggleWishlist,
  wishlistItems
}) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollability = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.8;
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  React.useEffect(() => {
    checkScrollability();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollability);
      return () => container.removeEventListener('scroll', checkScrollability);
    }
  }, [products]);

  if (!products || products.length === 0) {
    return null;
  }

  // Filter out the current product if it exists in the list
  const filteredProducts = products.filter((product, index, self) => 
    index === self.findIndex(p => p._id === product._id)
  );

  return (
    <div className="product-carousel-container">
      {title && (
        <div className="carousel-header">
          <h3 className="carousel-title">{title}</h3>
        </div>
      )}
      <div className="carousel-wrapper">
        {canScrollLeft && (
          <button
            className="carousel-button carousel-button-left"
            onClick={() => scroll('left')}
            aria-label={t('productCarousel.scrollLeft')}
          >
            <ChevronLeftIcon className="carousel-icon" />
          </button>
        )}
        <div
          ref={scrollContainerRef}
          className="carousel-scroll-container"
          onScroll={checkScrollability}
        >
          <div className="carousel-products">
            {filteredProducts.map((product) => (
              <div key={product._id} className="carousel-item">
                <ProductCard
                  product={product}
                  onAddToCart={onAddToCart}
                  onToggleWishlist={onToggleWishlist || (() => {})}
                  isInWishlist={wishlistItems?.includes(product._id) || false}
                />
              </div>
            ))}
          </div>
        </div>
        {canScrollRight && (
          <button
            className="carousel-button carousel-button-right"
            onClick={() => scroll('right')}
            aria-label={t('productCarousel.scrollRight')}
          >
            <ChevronRightIcon className="carousel-icon" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductCarousel;

